import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Spin, Alert, List } from 'antd';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ZAxis,
  LabelList,
  ResponsiveContainer,
} from 'recharts';
import { fetchClusterData } from './analysis.api';
import {COOL_COLOR_PALETTE} from '../../utils/colorUtils';

function ClusterAnalysis() {
  const dispatch = useDispatch();
  const clusterData = useSelector((state) => state.analysis.clusterData);
  const loading = useSelector((state) => state.analysis.clusterLoading);
  const error = useSelector((state) => state.analysis.clusterError);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const symbols = queryParams.get('symbols');
    const startDate = queryParams.get('start_date');
    const endDate = queryParams.get('end_date');

    dispatch(fetchClusterData({ symbols, start_date: startDate, end_date: endDate }));
  }, [dispatch]);

  if (loading === 'pending') {
    return <Spin size="large" />;
  }

  if (error) {
    return <Alert message="Error" description={error} type="error" />;
  }

  if (!clusterData) {
    return <div>No data available.</div>;
  }

  // Transform the data for Recharts
  const chartData = clusterData.flatMap((cluster, index) => {
    const clusterId = index + 1;
    return cluster.pca_coordinates.map((coords, coordIndex) => ({
      x: coords[0],
      y: coords[1],
      cluster: clusterId,
      ticker: cluster.tickers[coordIndex % cluster.tickers.length],
    }));
  });

  // Modern and bold color palette
  const colorPalette = [...COOL_COLOR_PALETTE];

  // Custom tooltip content
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip" style={{ backgroundColor: 'white', padding: '5px', border: '1px solid #ccc' }}>
          <p className="label">{`Ticker: ${payload[0].payload.ticker}`}</p>
          <p className="intro">{`Cluster: ${payload[0].payload.cluster}`}</p>
          <p className="desc">{`X: ${payload[0].value.toFixed(2)}, Y: ${payload[1].value.toFixed(2)}`}</p>
        </div>
      );
    }
    return null;
  };

  // Prepare data for the Ant Design list
  const listData = clusterData.flatMap((cluster, index) =>
    cluster.tickers.map((ticker) => ({
      ticker,
      cluster: index + 1,
      color: colorPalette[index % colorPalette.length],
    }))
  );

  return (
    <div className="p-4">
      <div className="flex flex-col 2xl:flex-row gap-4">
        <div className="2xl:flex-1">
          <h2 className="text-2xl font-medium mb-4 ml-5">Cluster Analysis Results</h2>
          <ResponsiveContainer width="100%" height={600}>
            <ScatterChart
                margin={{
                  top: 20,
                  right: 20,
                  bottom: 20,
                  left: 20,
                }}
            >
              <CartesianGrid stroke="#eeefff"/>
              <XAxis type="number" dataKey="x" name="PC1" unit="" stroke="#eeefff" tick={{fill: '#222'}}/>
              <YAxis type="number" dataKey="y" name="PC2" unit="" stroke="#eeefff" tick={{fill: '#222'}}/>
              <ZAxis type="number" dataKey="cluster" name="Cluster" range={[100, 500]}/>
              <Tooltip content={<CustomTooltip/>} cursor={{strokeDasharray: '3 3'}}/>

              {clusterData.map((cluster, index) => (
                  <Scatter
                      key={index}
                      name={`Cluster ${index + 1}`}
                      data={chartData.filter((data) => data.cluster === index + 1)}
                      fill={colorPalette[index % colorPalette.length]}
                  >
                    <LabelList
                        dataKey="ticker"
                        position="top"
                        style={{fontSize: '12px', fontWeight: 'bold', fill: colorPalette[index % colorPalette.length]}}
                    />
                  </Scatter>
              ))}
            </ScatterChart>
          </ResponsiveContainer>
        </div>

        <div className="2xl:w-[250px] 2xl:ml-4">
          <List
              size="small"
            className="max-h-[80vh] overflow-y-auto"
            dataSource={listData}
            renderItem={(item) => (
              <List.Item>
                <div className="flex items-center gap-2">
                  <span
                    className="inline-block w-2 h-2 rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-sm font-medium">
                    {item.ticker} ({item.cluster})
                  </span>
                </div>
              </List.Item>
            )}
          />
        </div>
      </div>
    </div>
  );
}

export default ClusterAnalysis;